import { useEffect, useState } from "react";
import api, { fileUrl } from "../../lib/api";
import { Plus, Trash2, Pencil, Save, X, Upload as UploadIcon, Sparkles, Copy } from "lucide-react";

const empty = { title: "", caption: "", image_url: "", category: "General", animation_type: "", order: 0, published: true };
const ANIMATIONS = [
    { value: "", label: "(Use site default)" },
    { value: "fade", label: "Fade" },
    { value: "slide", label: "Slide" },
    { value: "zoom", label: "Zoom" },
    { value: "none", label: "None" },
];

export default function GalleryEditor() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/gallery?all=true").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const save = async (g) => {
        const payload = { ...g, order: parseInt(g.order) || 0, animation_type: g.animation_type || null };
        if (g.id) await api.put(`/gallery/${g.id}`, payload);
        else await api.post("/gallery", payload);
        setEditing(null); load();
    };
    const del = async (id) => { if (!window.confirm("Delete photo?")) return; await api.delete(`/gallery/${id}`); load(); };
    const dup = async (id) => { await api.post(`/gallery/${id}/duplicate`); load(); };

    return (
        <div data-testid="gallery-editor">
            <div className="flex items-center justify-between mb-8">
                <div><h1 className="font-display text-4xl mb-2">Photo Gallery</h1><p className="text-muted-foreground">Manage the public photo gallery. Each photo can use its own animation style or fall back to the site default.</p></div>
                <button onClick={() => setEditing({ ...empty })} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium" data-testid="add-gallery"><Plus size={14} /> New Photo</button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((g) => (
                    <div key={g.id} className="bg-card border border-border rounded-sm overflow-hidden" data-testid={`gallery-row-${g.id}`}>
                        <div className="aspect-[4/3] bg-background overflow-hidden">
                            {g.image_url ? <img src={fileUrl(g.image_url)} alt={g.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-muted" />}
                        </div>
                        <div className="p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] uppercase tracking-[0.22em] text-primary">{g.category}</span>
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                    <Sparkles size={10} /> {g.animation_type || "default"}
                                </span>
                            </div>
                            {g.title && <h3 className="font-display text-base leading-tight mb-1">{g.title}</h3>}
                            {g.caption && <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{g.caption}</p>}
                            <div className="flex gap-2 mt-3">
                                <button onClick={() => setEditing({ ...g, animation_type: g.animation_type || "" })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs"><Pencil size={11} /> Edit</button>
                                <button onClick={() => dup(g.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs" data-testid={`dup-gallery-${g.id}`}><Copy size={11} /> Duplicate</button>
                                <button onClick={() => del(g.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-destructive text-destructive text-xs"><Trash2 size={11} /> Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {items.length === 0 && <div className="text-center text-muted-foreground py-20">No photos yet.</div>}

            {editing && <Modal item={editing} onChange={setEditing} onSave={save} onClose={() => setEditing(null)} />}
        </div>
    );
}

const Modal = ({ item, onChange, onSave, onClose }) => {
    const set = (k, v) => onChange({ ...item, [k]: v });
    const upload = async (file) => {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        set("image_url", data.url);
    };
    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-start justify-center p-6 overflow-y-auto" data-testid="gallery-modal">
            <div className="bg-card border border-border rounded-sm w-full max-w-2xl my-10">
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <h2 className="font-display text-2xl">{item.id ? "Edit Photo" : "New Photo"}</h2>
                    <button onClick={onClose} className="p-2 rounded-sm hover:bg-muted"><X size={18} /></button>
                </div>
                <div className="p-6 space-y-5">
                    <F label="Title" value={item.title} onChange={(v) => set("title", v)} testid="g-title" />
                    <T label="Caption" value={item.caption} onChange={(v) => set("caption", v)} testid="g-caption" />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <F label="Category" value={item.category} onChange={(v) => set("category", v)} testid="g-category" />
                        <F label="Order" type="number" value={item.order} onChange={(v) => set("order", v)} testid="g-order" />
                    </div>
                    <label className="block">
                        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Animation (per-photo override)</span>
                        <select value={item.animation_type || ""} onChange={(e) => set("animation_type", e.target.value)} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" data-testid="g-animation">
                            {ANIMATIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                        </select>
                    </label>
                    <div>
                        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Image *</span>
                        <div className="flex items-center gap-3 mb-3">
                            <input value={item.image_url || ""} onChange={(e) => set("image_url", e.target.value)} placeholder="https://… or /api/files/…"
                                className="flex-1 px-3 py-2.5 rounded-sm border border-border bg-background text-sm" data-testid="g-image-url" />
                            <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-border cursor-pointer text-sm whitespace-nowrap"><UploadIcon size={14} /> Upload
                                <input type="file" className="hidden" accept="image/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="g-image-upload" />
                            </label>
                        </div>
                        {item.image_url && <img src={fileUrl(item.image_url)} alt="" className="h-32 rounded-sm border border-border object-cover" />}
                    </div>
                    <label className="inline-flex items-center gap-2"><input type="checkbox" checked={!!item.published} onChange={(e) => set("published", e.target.checked)} data-testid="g-published" /><span className="text-sm">Published</span></label>
                </div>
                <div className="p-6 border-t border-border flex justify-end gap-3">
                    <button onClick={onClose} className="px-5 py-2.5 rounded-sm border border-border text-sm">Cancel</button>
                    <button onClick={() => onSave(item)} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium" data-testid="g-save"><Save size={14} /> Save</button>
                </div>
            </div>
        </div>
    );
};

const F = ({ label, value, onChange, testid, type = "text" }) => (
    <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" /></label>
);
const T = ({ label, value, onChange, testid }) => (
    <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm resize-y" /></label>
);
